import localesJson from './locales.json';

export const SUPPORTED_COUNTRY_CODES_ARR = [
  'KR',
  'US',
  'JP',
  'ID',
  'TH',
  'PH',
  'VN',
  'MY',
  'UZ',
  'FR',
  'KH',
  'RU',
  'CN',
  'TW',
  'ES',
  'PT',
  'DE',
  'IT',
  'TR',
  'SG',
  'BN',
  'BR',
  'NL',
  'PL',
  'IN',
  'EE',
  'HK',
] as const;

export type SupportedCountryCode = (typeof SUPPORTED_COUNTRY_CODES_ARR)[number];
export function isSupportedCountryCode(
  code: string,
): code is SupportedCountryCode {
  return SUPPORTED_COUNTRY_CODES_ARR.includes(code as any);
}
// 기타 국가 코드가 생겼습니다
export const DEFAULT_COUNTRY_CODE = 'ETC';
export const DEFAULT_LOCALE_CODE = 'en';
/**
 *  없을경우 기본 US 코드를 반환합니다.
 * */
export const getSupportedCountryCode = (countryCode?: string) => {
  return (
    SUPPORTED_COUNTRY_CODES_ARR.find(
      country => country === countryCode?.toUpperCase(),
    ) ?? DEFAULT_COUNTRY_CODE
  );
};

export const getLocaleCodeByCountryCode = (countryCode?: string) => {
  const supportedCountryCode = getSupportedCountryCode(countryCode);

  // 'ETC'인 경우 기본 로케일 반환
  if (supportedCountryCode === DEFAULT_COUNTRY_CODE) {
    return DEFAULT_LOCALE_CODE;
  }

  return (
    COUNTRY_CODE_TO_LOCALE_MAP[supportedCountryCode as SupportedCountryCode] ??
    DEFAULT_LOCALE_CODE
  );
};

export const SUPPORTED_NOTICE_COUNTRY_ARR: SupportedCountryCode[] = [
  'KR',
  'US',
  'JP',
  'ID',
  'TH',
  'PH',
  'VN',
  'MY',
  'UZ',
  'FR',
  'KH',
  'RU',
  'CN',
  'TW',
  'ES',
  'PT',
  'DE',
  'IT',
  'TR',
  'SG',
  'BN',
  'BR',
  'NL',
  'PL',
  'IN',
  'EE',
  'HK',
];

export const SOUTHEAST_ASIA_COUNTRY_CODES = [
  'ID',
  'TH',
  'PH',
  'VN',
  'MY',
  'SG',
  'TW',
  'BN',
];

export const getSupportedNoticeCountryCode = (countryCode: string) => {
  return (
    SUPPORTED_NOTICE_COUNTRY_ARR.find(
      country => country === countryCode.toUpperCase(),
    ) ?? DEFAULT_COUNTRY_CODE
  );
};

/** 언어가 추가되는 변수입니다.
 * ./locales.json 파일을 수정하세요.
 * 아래 슬랙을 참고해 필요한 다른 작업들도 병행해 주세요.
 * https://zep-us.slack.com/archives/C07374E939S/p1734357871197999?thread_ts=1733914597.693229&cid=C07374E939S
 */
export const SUPPORTED_LOCALES_ARR =
  localesJson.locales as unknown as readonly [
    'ko',
    'en',
    'ja',
    'id',
    'th',
    'fil',
    'vi',
    'ms',
    'uz',
    'fr',
    'km',
    'ru',
    'zh-CN',
    'zh-TW',
    'es',
    'pt',
    'de',
    'it',
    'tr',
    'en-SG',
    'pt-BR',
    'nl',
    'pl',
    'hi-IN',
    'bn-IN',
    'te-IN',
    'pa-IN',
    'ml-IN',
    'ta-IN',
    'et',
    'zh-HK',
  ];

export const SUPPORTED_LOCALES_OBJ = Object.fromEntries(
  SUPPORTED_LOCALES_ARR.map(l => [l, l]),
) as { [K in (typeof SUPPORTED_LOCALES_ARR)[number]]: K };

export const LOCALE_MAP: Record<string, SupportedLocale> = {
  'zh-Hans': 'zh-CN',
  'zh-Hant': 'zh-TW',
};

export type SupportedLocalesObject = typeof SUPPORTED_LOCALES_OBJ;
export type SupportedLocalesArray = typeof SUPPORTED_LOCALES_ARR;

export type SupportedLocale = SupportedLocalesArray[number];

/**
 * @example
 * SupportedLocale = 'ko' | 'en' | 'ja' | 'id' 일 때,
 * SupportedLocaleMap<'ko' | 'en' | 'ja', string>는 다음과 같습니다:
 * {
 *   ko: string;
 *   en: string;
 *   ja: string;
 *   id?: string; // 'id'는 선택적입니다.
 * }
 */
export type SupportedLocaleMap<
  T extends SupportedLocale = SupportedLocale,
  U = string,
> = Record<T, U> & Partial<Record<Exclude<SupportedLocale, T>, U>>;

export const COUNTRY_CODE_TO_LOCALE_MAP = {
  KR: 'ko',
  US: 'en',
  JP: 'ja',
  ID: 'id',
  TH: 'th',
  PH: 'fil',
  VN: 'vi',
  MY: 'ms',
  UZ: 'uz',
  FR: 'fr',
  KH: 'km',
  RU: 'ru',
  CN: 'zh-CN',
  TW: 'zh-TW',
  ES: 'es',
  PT: 'pt',
  DE: 'de',
  IT: 'it',
  TR: 'tr',
  SG: 'en-SG',
  BN: 'ms',
  BR: 'pt-BR',
  NL: 'nl',
  PL: 'pl',
  IN: 'hi-IN',
  EE: 'et',
  HK: 'zh-HK',
} as const satisfies Record<SupportedCountryCode, SupportedLocale>;

export const LOCALE_TO_COUNTRY_CODE_MAP = {
  ko: 'KR',
  en: 'US',
  ja: 'JP',
  id: 'ID',
  th: 'TH',
  fil: 'PH',
  vi: 'VN',
  ms: 'MY',
  uz: 'UZ',
  fr: 'FR',
  km: 'KH',
  ru: 'RU',
  'zh-CN': 'CN',
  'zh-TW': 'TW',
  es: 'ES',
  pt: 'PT',
  de: 'DE',
  it: 'IT',
  tr: 'TR',
  'en-SG': 'SG',
  'pt-BR': 'BR',
  nl: 'NL',
  pl: 'PL',
  'hi-IN': 'IN',
  'bn-IN': 'IN',
  'te-IN': 'IN',
  'pa-IN': 'IN',
  'ml-IN': 'IN',
  'ta-IN': 'IN',
  et: 'EE',
  'zh-HK': 'HK',
} as const satisfies Record<SupportedLocale, SupportedCountryCode>;

export type CountryCodeToLocaleMap = typeof COUNTRY_CODE_TO_LOCALE_MAP;
export type LocaleToCountryCodeMap = typeof LOCALE_TO_COUNTRY_CODE_MAP;
